/**
 * i18n Configuration
 * Internationalization setup using react-i18next
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import vi from './locales/vi.json';

// Language storage key
const LANGUAGE_STORAGE_KEY = 'app-language';

// Get stored language or detect from browser
const getInitialLanguage = (): string => {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && ['en', 'vi'].includes(stored)) {
        return stored;
    }
    // Detect from browser
    const browserLang = navigator.language.split('-')[0];
    return browserLang === 'vi' ? 'vi' : 'en';
};

// Resources
const resources = {
    en: { translation: en },
    vi: { translation: vi },
};

// Initialize i18n
i18n.use(initReactI18next).init({
    resources,
    lng: getInitialLanguage(),
    fallbackLng: 'en',
    interpolation: {
        escapeValue: false, // React already escapes values
    },
    react: {
        useSuspense: false,
    },
});

// Save language preference when changed
i18n.on('languageChanged', (lng) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
    document.documentElement.setAttribute('lang', lng);
});

// Set initial lang attribute
document.documentElement.setAttribute('lang', i18n.language);

export default i18n;

// Export helper for changing language
export const changeLanguage = (lang: 'en' | 'vi') => {
    i18n.changeLanguage(lang);
};

// Export supported languages
export const supportedLanguages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
] as const;

/**
 * Primary tag for `Accept-Language` on API calls. Backend uses the first tag only
 * (see `parse_accept_language`); must align with app language, not the browser default.
 */
export function getApiAcceptLanguage(): string {
    const raw = (i18n.language || 'en').split('-')[0]?.toLowerCase() || 'en';
    return raw === 'vi' ? 'vi' : 'en';
}

